/* eslint-disable no-param-reassign */
/* eslint-disable react/jsx-filename-extension */
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Students model used to store the students data
 * @file Students.js
 * @author Mohamed yasar arabath M
 */

import { types, applySnapshot, getSnapshot } from 'mobx-state-tree';
import Framework from '@greenville/framework';
import { COURSE_USER_ROLE, COURSE_USER_STATUS } from '../../../common/constants';
import { AET_STORE_EVENTS } from '../common/Constants';
import CommonUtils from '../../../common/utils/CommonUtils';

const StudentProductEntitlements = types.model('StudentProductEntitlements', {
  paymentMethodType: types.maybeNull(types.string),
  expired: types.maybeNull(types.boolean),
  start_date: types.maybeNull(types.string),
  end_date: types.maybeNull(types.string)
});

const Student = types.model({
  userId: types.maybeNull(types.string),
  firstName: types.maybeNull(types.string),
  lastName: types.maybeNull(types.string),
  email: types.maybeNull(types.string),
  userName: types.maybeNull(types.string),
  userType: types.maybeNull(types.string),
  status: types.maybeNull(types.string),
  createddate: types.maybeNull(types.string),
  groupType: types.maybeNull(types.string),
  isInstructor: types.optional(types.boolean, false),
  product_entitlements: types.maybeNull(StudentProductEntitlements)
});

const mapStudentList = (list = []) => list.map(student => ({
  ...student,
  isInstructor: student.groupType === COURSE_USER_ROLE.INSTRUCTOR
}));

const isActiveStudent = (user) => {
  const status = String(user?.status || '').toLowerCase();
  const groupType = String(user?.groupType || '').toLowerCase();

  return CommonUtils.localStringCompare(status, COURSE_USER_STATUS.ACTIVE)
    && CommonUtils.localStringCompare(groupType, COURSE_USER_ROLE.STUDENT);
};

const StudentsModel = types.model({
  students: types.optional(types.array(Student), []),
  allUsers: types.optional(types.array(Student), [])
}).actions(self => ({
  fetchStudents(courseId) {
    Framework.getEventManager().publish(AET_STORE_EVENTS.STUDENTS_ROSTER_REQUESTED, { courseId, isRoster: false });
  },
  /**
   * Fetch all users of a course
   * @param {string} courseId - The ID of the course
   * @param {string} [productId] - Product ID from bookshelf
   * @returns {void}
   */
  fetchAllUsersOfCourse(courseId, productId) {
    Framework.getEventManager().publish(AET_STORE_EVENTS.STUDENTS_ROSTER_REQUESTED, {
      courseId,
      productId,
      isRoster: false,
      includeAllUsers: true
    });
  },
  /**
   * Set the students list
   * @param {array} data - The students data
   * @returns {void}
   */
  setStudents(data) {
    const list = Array.isArray(data) ? data : data?.students;

    applySnapshot(self.students, mapStudentList(list || []));
  },
  /**
   * Set all users of a course (active/inactive students, TAs, instructors)
   * @param {array} data - The users data
   * @returns {void}
   */
  setAllUsers(data) {
    const list = Array.isArray(data) ? data : data?.students;

    applySnapshot(self.allUsers, mapStudentList(list || []));
    self.syncActiveStudentsFromAllUsers();
  },
  /**
   * Derive active students from allUsers (status active + role student)
   * @returns {void}
   */
  syncActiveStudentsFromAllUsers() {
    const activeStudents = getSnapshot(self.allUsers).filter(isActiveStudent);

    applySnapshot(self.students, activeStudents);
  },
  /**
   * Reset the students list
   * @returns {void}
   */
  resetStudents() {
    self.students.clear();
    self.allUsers.clear();
  },
  /**
   * Change the user role
   * @param {string} userId - The ID of the user
   * @param {string} sectionId - The ID of the section
   * @param {string} status - The status of the user
   * @param {string} groupType - The group type of the user
   * @returns {void}
   */
  changeUserRole(userId, sectionId, status, groupType, productId) {
    const currentUser = self.findAllUserById(userId);

    if (currentUser?.groupType === groupType) {
      return;
    }

    Framework.getEventManager().publish(
      AET_STORE_EVENTS.CHANGE_COURSE_USER_ROLE_REQUESTED,
      {
        payload: [{
          userId,
          sectionId,
          status,
          groupType
        }],
        sectionId,
        productId
      }
    );
  },
  /**
   * Change the user status
   * @param {string} userId - The ID of the user
   * @param {string} sectionId - The ID of the section
   * @param {string} status - The status of the user
   * @param {string} groupType - The group type of the user
   * @returns {void}
   */
  changeUserStatus(userId, sectionId, status, groupType, productId) {
    const currentUser = self.findAllUserById(userId);

    if (currentUser?.status === status) {
      return;
    }

    Framework.getEventManager().publish(
      AET_STORE_EVENTS.CHANGE_COURSE_USER_ROLE_REQUESTED,
      {
        payload: [{
          userId,
          sectionId,
          status,
          groupType
        }],
        sectionId,
        productId
      }
    );
  },
  /**
   * Bulk change the user statuses
   * @param {Array} payload - The payload of the users
   * @returns {void}
   */
  bulkChangeUserStatuses(payload, sectionId, productId) {
    if (!payload?.length) {
      return;
    }

    Framework.getEventManager().publish(
      AET_STORE_EVENTS.CHANGE_COURSE_USER_ROLE_REQUESTED,
      { payload, sectionId, productId }
    );
  }
})).views(self => ({
  findStudentById(userId) {
    const studentsSnapshot = getSnapshot(self.students);

    return studentsSnapshot.find(student => student.userId === userId);
  },
  findAllUserById(userId) {
    const allUsersSnapshot = getSnapshot(self.allUsers);

    return allUsersSnapshot.find(user => user.userId === userId);
  },
  get studentsList() {
    return getSnapshot(self.students);
  },
  get allUsersList() {
    return getSnapshot(self.allUsers);
  },
  getAllUsersOfCourse() {
    const allUsersSnapshot = [...(getSnapshot(self.allUsers) || [])];

    if (!allUsersSnapshot.length) {
      return [];
    }

    return allUsersSnapshot.sort((a, b) => (
      (a.firstName || '').localeCompare(b.firstName || '')
    ));
  }
}));

export default StudentsModel;
